import React, {
	FC,
	useEffect,
	useMemo,
	useState,
	useContext,
} from "react";
import { useDropzone } from "react-dropzone";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	SessionType,
	TrainingDrillRequest,
} from "@/features/training/model/Training/TrainingDrill";
import { TrainingKpi } from "@/features/training/model/Training/TrainingKpi";
import {
	CustomCloseButton,
	CustomSaveButton,
	KpiCheckboxesGroup,
} from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import CustomSelect from "@/shared/forms/CustomSelect";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import {
	useMutateCreateTrainingDrill,
	useMutateCreateTrainingFile,
} from "@/service/TrainingMutations";
import {
	useFetchCourseFolders,
	useFetchTrainingKpis,
	useFetchUserSummariesByCurrentCompany,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface NewTrainingDrillProps {
	type: SessionType;
	handleClose: () => void;
	open: boolean;
}

interface CheckboxState extends TrainingKpi {
	checked: boolean;
}

const ACCEPTED_TYPES = {
	"application/pdf": [".pdf"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
		".docx",
	],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/png": [".png"],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

// ---- helpers ---------------------------------------------------------------

/** Safely normalize unknown API shapes to an array (common keys supported). */
function toArray<T>(maybeArray: unknown): T[] {
	if (Array.isArray(maybeArray)) return maybeArray as T[];
	if (typeof maybeArray !== "object" || maybeArray == null) return [];
	const record = maybeArray as Record<string, unknown>;
	if (Array.isArray(record["items"])) return record["items"] as T[];
	if (Array.isArray(record["Items"])) return record["Items"] as T[];
	if (Array.isArray(record["data"])) return record["data"] as T[];
	if (Array.isArray(record["results"])) return record["results"] as T[];
	return [];
}

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const NewTrainingDrill: FC<NewTrainingDrillProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isExercise = props.type === SessionType.Exercise;
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="lg"
			fullScreen={isMobile}
			aria-labelledby="new-drill-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="new-drill-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.DrillTab.PanelLeft.Actions.NewTrainingDrill.L148C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{
						fontWeight: 700,
						color: "text.primary",
						pr: 5,
						mb: 0.75,
					}}
				>
					<Translate
						String={
							isExercise
								? "Training.Drills.NewDrill.Exercise.Title"
								: "Training.Drills.NewDrill.Event.Title"
						}
					>
						{isExercise ? "New Exercise" : "New Event"}
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block" }}
				>
					<Translate
						String={
							isExercise
								? "Training.Drills.NewDrill.Exercise.Subtitle"
								: "Training.Drills.NewDrill.Event.Subtitle"
						}
					>
						{isExercise
							? "Add a training exercise with details and KPI parameters."
							: "Schedule a drill event with details and KPI parameters."}
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelLeft.Actions.NewTrainingDrill.L189C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						...closeButtonSx,
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				<NewTrainingDrillForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

export const NewTrainingDrillForm: FC<NewTrainingDrillProps> = (props) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [newAttachment, setNewAttachment] = useState<{
		id: number;
		name: string;
		size: number;
	} | null>(null);

	const { data: foldersRaw, isLoading: isLoadingFolderOptions } =
		useFetchCourseFolders();
	const { data: trainersRaw, isLoading: isLoadingTrainerOptions } =
		useFetchUserSummariesByCurrentCompany();
	const { data: kpisRaw, isLoading: isLoadingKpiOptions } =
		useFetchTrainingKpis();

	const { createTrainingFile, isSubmitting: isUploadingFile } =
		useMutateCreateTrainingFile();
	const createTrainingDrill = useMutateCreateTrainingDrill();

	// Normalize API shapes defensively so .map/.[0] are always safe.
	const folders = useMemo(
		() => toArray<{ Id: number; Name: string }>(foldersRaw),
		[foldersRaw],
	);
	const trainerOptions = useMemo(
		() => toArray<{ Id: number; Name: string }>(trainersRaw),
		[trainersRaw],
	);
	const kpiOptions = useMemo(() => toArray<TrainingKpi>(kpisRaw), [kpisRaw]);

	const [checkboxesState, setCheckboxesState] =
		useState<Record<number, CheckboxState>>();

	useEffect(() => {
		if (kpiOptions && kpiOptions.length) {
			const next: Record<number, CheckboxState> = kpiOptions.reduce(
				(state, current) => {
					state[current.Id] = { ...current, checked: false };
					return state;
				},
				{} as Record<number, CheckboxState>,
			);
			setCheckboxesState(next);
		} else {
			setCheckboxesState({});
		}
	}, [kpiOptions]);

	const handleSave = async (request: TrainingDrillRequest) => {
		// coerce types from <select>
		request.TrainerId = Number(request.TrainerId) || 0;
		request.FolderId = Number(request.FolderId) || 0;

		request.KpiIds = checkboxesState
			? Object.values(checkboxesState)
					.filter((k) => k.checked)
					.map((k) => k.Id)
			: [];

		if (newAttachment) {
			request.AttachmentIds = [
				...(request.AttachmentIds ?? []),
				newAttachment.id,
			];
		}

		try {
			await createTrainingDrill(request);
			props.handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		}
	};

	const uploadFile = async (file: File) => {
		try {
			const response = await createTrainingFile({ File: file });
			setNewAttachment({ id: response.Id, name: file.name, size: file.size });
		} catch (error) {
			console.error(error);
			await AlertToast.failed("Failed to upload attachment");
		}
	};

	const removeAttachment = () => {
		setNewAttachment(null);
	};

	const preventFocusLose = (e: React.KeyboardEvent) => {
		e.stopPropagation();
	};
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (file) void uploadFile(file);
		},
		accept: ACCEPTED_TYPES,
		maxSize: MAX_FILE_SIZE,
		maxFiles: 1,
		multiple: false,
		disabled: isUploadingFile,
		onDropRejected: (rejections) => {
			const err = rejections[0]?.errors?.[0];
			if (err?.code === "file-too-large") {
				void AlertToast.failed("File exceeds 5 MB limit");
			} else if (err?.code === "file-invalid-type") {
				void AlertToast.failed("Invalid file type. Use PDF, DOC, JPG or PNG.");
			}
		},
	});

	const isLoadingAny =
		isLoadingFolderOptions ||
		isLoadingTrainerOptions ||
		isLoadingKpiOptions ||
		!checkboxesState;

	if (isLoadingAny) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	const validationSchema = Yup.object().shape({
		Name: Yup.string().required("Name is required"),
	});
	const sectionCardSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 2, md: 3 },
	};

	// defaults derived from normalized arrays
	const defaultTrainerId = trainerOptions[0]?.Id ?? 0;
	const defaultFolderId = folders[0]?.Id ?? 0;

	return (
		<>
			<Formik
				initialValues={{
					Name: "",
					Description: "",
					TrainerId: defaultTrainerId,
					FolderId: defaultFolderId,
					AttachmentIds: [] as number[],
					KpiIds: [] as number[],
					Type: props.type,
				}}
				validationSchema={validationSchema}
				onSubmit={handleSave}
			>
				{({ values, isSubmitting }) => (
					<Form>
						<Grid container spacing={3}>
							{/* Left column — Details */}
							<Grid item xs={12} md={7}>
								<Paper variant="outlined" sx={{ ...sectionCardSx, mb: 2 }}>
									<Typography
										variant="subtitle1"
										fontWeight={700}
										sx={{ mb: 1 }}
									>
										<Translate String="Training.Drill.NewDrill.Section.Details">
											Details
										</Translate>
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", mb: 2 }}
									>
										<Translate String="Training.Drill.NewDrill.Section.Details.Desc">
											Name, description, trainer and folder.
										</Translate>
									</Typography>
									<Stack spacing={2}>
										<CustomInput
											id="name"
											type="text"
											label="Name"
											name="Name"
											translationString="Training.Drill.NewDrill.Name"
											onKeyDown={preventFocusLose}
											style={{ width: "100%" }}
										/>
										<SATOSEditorField
											id="description"
											name="Description"
											label="Description"
											disabled={isSubmitting}
											aria-required={true}
											aria-describedby="description-error-text"
											style={{ minWidth: "100%" }}
											imageUploadDisabled={true}
										/>
										<CustomSelect
											id="trainerId"
											label="Trainer"
											name="TrainerId"
											translationString="Training.Drill.NewDrill.Trainer"
											defaultValue={values.TrainerId}
											style={{ width: "100%" }}
										>
											{trainerOptions.map((x) => (
												<option value={x.Id} key={x.Id}>
													{x.Name}
												</option>
											))}
										</CustomSelect>
										<CustomSelect
											id="folderId"
											label="Folder"
											name="FolderId"
											translationString="Training.Drill.NewDrill.Folder"
											defaultValue={values.FolderId}
											style={{ width: "100%" }}
										>
											{folders.map((x) => (
												<option value={x.Id} key={x.Id}>
													{x.Name}
												</option>
											))}
										</CustomSelect>
									</Stack>
								</Paper>

								<Paper variant="outlined" sx={sectionCardSx}>
									<Typography
										variant="subtitle1"
										fontWeight={700}
										sx={{ mb: 1 }}
									>
										<Translate String="Training.EditCourse.Attachments">
											Attachments
										</Translate>
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", mb: 2 }}
									>
										<Translate String="Training.EditCourse.Attachments.Desc">
											Upload reference files or supporting material.
										</Translate>
									</Typography>
									<Box
										{...getRootProps()}
										sx={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											border: "2px dashed",
											borderColor: isDragActive ? "primary.main" : "divider",
											borderRadius: 2,
											py: 3,
											px: 2,
											textAlign: "center",
											cursor: isUploadingFile ? "progress" : "pointer",
											bgcolor: isDragActive ? "action.hover" : "background.default",
											color: "text.secondary",
											"&:hover": {
												borderColor: "primary.main",
												bgcolor: "action.hover",
											},
										}}
									>
										<input {...getInputProps()} id="new-drill-attachment" />
										{newAttachment ? (
											<Stack
												direction="row"
												alignItems="center"
												spacing={2}
												sx={{
													p: 2,
													borderRadius: 1,
													bgcolor: "background.paper",
													width: "100%",
													maxWidth: 400,
													border: "1px solid",
													borderColor: "divider",
												}}
											>
												<InsertDriveFileOutlinedIcon
													sx={{ color: "text.secondary", fontSize: 32 }}
												/>
												<Box sx={{ flex: 1, minWidth: 0 }}>
													<Typography
														variant="body2"
														sx={{
															fontWeight: 600,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{newAttachment.name}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{formatFileSize(newAttachment.size)}
													</Typography>
												</Box>
												<IconButton
													onClick={(e) => {
														e.stopPropagation();
														removeAttachment();
													}}
													color="error"
													size="small"
													aria-label="Remove file"
												>
													<CloseIcon fontSize="small" />
												</IconButton>
											</Stack>
										) : (
											<>
												<Typography variant="body2" sx={{ mb: 0.5 }}>
													<Translate String="Training.NewTrainingCourse.DropZone.Drag">
														Drag & drop files here
													</Translate>
												</Typography>
												<Typography variant="body2">
													<Translate String="Training.NewTrainingCourse.DropZone.Or">
														or
													</Translate>{" "}
													<Box
														component="span"
														sx={{ textDecoration: "underline", color: "primary.main" }}
													>
														<Translate String="Shift.Event.Choose.File">
															Choose files
														</Translate>
													</Box>
												</Typography>
											</>
										)}
									</Box>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.5, display: "block" }}
									>
										<Translate String="Shift.Event.File.Help">
											PDF, DOC, JPG, PNG · max 5 MB
										</Translate>
									</Typography>
								</Paper>
							</Grid>

							{/* Right column — KPI Parameters */}
							<Grid item xs={12} md={5}>
								<Paper variant="outlined" sx={sectionCardSx}>
									<KpiCheckboxesGroup
										checkboxesState={checkboxesState}
										setCheckboxesState={setCheckboxesState}
									/>
								</Paper>
							</Grid>
						</Grid>

						<DialogActions
							sx={{
								...sharedTaskDialogActionsSx(theme, isMobile),
								px: 0,
								pt: 2,
								mt: 3,
								display: "flex",
								justifyContent: "flex-end",
								gap: 1.5,
								flexWrap: "wrap",
							}}
						>
							<CustomCloseButton
								handleClose={props.handleClose}
								disabled={isSubmitting || isUploadingFile}
							/>
							<CustomSaveButton
								loading={isSubmitting}
								disabled={isSubmitting || isUploadingFile}
							/>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</>
	);
};
